/**
 * ChatPanelHeader - Conversation title with dropdown menu
 *
 * Shows the conversation name with a chevron. Click opens a dropdown
 * with Renombrar, Fijar/Desfijar, Archivar. Inline edit on rename.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, PanelLeft, Pencil, Pin, Archive, Copy, PenSquare } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

export interface ChatPanelHeaderProps {
  conversationName?: string;
  isPinned?: boolean;
  onRename?: (name: string) => Promise<void>;
  onPin?: (pinned: boolean) => Promise<void>;
  onArchive?: () => Promise<void>;
  onCopy?: () => void;
  // Sidebar toggle (always visible on desktop, click to open/close)
  isSidebarOpen?: boolean;
  onToggleSidebar?: () => void;
  onNewConversation?: () => void;
}

export function ChatPanelHeader({
  conversationName,
  isPinned = false,
  onRename,
  onPin,
  onArchive,
  onCopy,
  isSidebarOpen = true,
  onToggleSidebar,
  onNewConversation,
}: ChatPanelHeaderProps): React.ReactElement {
  const { theme } = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const displayName = conversationName || 'Nueva conversación';
  const hasActions = onRename || onPin || onArchive || onCopy;

  // Focus input on edit
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      if (triggerRef.current?.contains(e.target as Node)) return;
      setMenuOpen(false);
    };
    const timer = setTimeout(() => document.addEventListener('click', handleClick), 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClick);
    };
  }, [menuOpen]);

  // Close menu on Escape
  useEffect(() => {
    if (!menuOpen && !isEditing) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        setIsEditing(false);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [menuOpen, isEditing]);

  const startEditing = useCallback(() => {
    setEditValue(conversationName || '');
    setIsEditing(true);
    setMenuOpen(false);
  }, [conversationName]);

  const saveEdit = useCallback(async () => {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== conversationName && onRename) {
      await onRename(trimmed);
    }
    setIsEditing(false);
  }, [editValue, conversationName, onRename]);

  const handlePin = useCallback(async () => {
    await onPin?.(!isPinned);
    setMenuOpen(false);
  }, [onPin, isPinned]);

  const handleArchive = useCallback(async () => {
    await onArchive?.();
    setMenuOpen(false);
  }, [onArchive]);

  const handleCopy = useCallback(() => {
    onCopy?.();
    setMenuOpen(false);
  }, [onCopy]);

  // Menu position
  const getMenuStyle = useCallback((): React.CSSProperties => {
    if (!triggerRef.current) return { top: 0, left: 0 };
    const rect = triggerRef.current.getBoundingClientRect();
    return { top: rect.bottom + 4, left: rect.left };
  }, []);

  return (
    <div className="flex-shrink-0 px-4 py-2 flex items-center gap-2 h-10">
      {/* Sidebar toggle — desktop only, always visible */}
      {onToggleSidebar && (
        <button
          className={`max-md:hidden p-0.5 -ml-2 rounded min-h-[44px] min-w-[44px] flex items-center justify-center ${theme.colors.text.muted} opacity-50 hover:opacity-100 transition-opacity`}
          onClick={onToggleSidebar}
          aria-label={isSidebarOpen ? 'Cerrar sidebar' : 'Abrir sidebar'}
        >
          <PanelLeft className="w-4 h-4" />
        </button>
      )}
      {isEditing ? (
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') saveEdit();
            if (e.key === 'Escape') setIsEditing(false);
          }}
          onBlur={saveEdit}
          className={`flex-1 min-w-0 text-sm font-medium px-2 py-0.5 rounded border ${theme.colors.border.focus} ${theme.colors.bg.input} ${theme.colors.text.primary} outline-none`}
        />
      ) : (
        <button
          ref={triggerRef}
          onClick={() => hasActions && setMenuOpen(!menuOpen)}
          className={`flex items-center gap-1 min-w-0 hover:${theme.colors.bg.secondary} rounded px-2 py-0.5 -mx-2 transition-colors min-h-[44px]`}
          aria-label="Opciones de conversación"
        >
          <span className={`text-sm font-medium truncate ${theme.colors.text.primary}`}>
            {displayName}
          </span>
          {hasActions && (
            <ChevronDown
              className={`w-3.5 h-3.5 flex-shrink-0 ${
                theme.colors.text.muted
              } transition-transform ${menuOpen ? 'rotate-180' : ''}`}
            />
          )}
        </button>
      )}

      {/* Spacer to push new-conversation icon to the right */}
      <div className="flex-1" />

      {/* New conversation shortcut */}
      {onNewConversation && (
        <button
          onClick={onNewConversation}
          className={`p-1 min-h-[44px] min-w-[44px] flex items-center justify-center rounded ${theme.colors.text.muted} opacity-50 hover:opacity-100 transition-opacity -mr-2`}
          aria-label="Nueva conversación"
          title="Nueva conversación"
        >
          <PenSquare className="w-4 h-4" />
        </button>
      )}

      {/* Dropdown menu portal */}
      {menuOpen &&
        hasActions &&
        createPortal(
          <div
            ref={menuRef}
            className={`fixed w-48 rounded-lg shadow-lg border z-[9999] py-1 ${theme.colors.bg.secondary} ${theme.colors.border.primary}`}
            style={getMenuStyle()}
          >
            {onRename && (
              <button
                onClick={startEditing}
                className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${theme.colors.state.hover}`}
              >
                <Pencil className="w-3.5 h-3.5" /> Renombrar
              </button>
            )}
            {onPin && (
              <button
                onClick={handlePin}
                className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${theme.colors.state.hover}`}
              >
                <Pin className="w-3.5 h-3.5" /> {isPinned ? 'Desfijar' : 'Fijar'}
              </button>
            )}
            {onCopy && (
              <button
                onClick={handleCopy}
                className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${theme.colors.state.hover}`}
              >
                <Copy className="w-3.5 h-3.5" /> Copiar
              </button>
            )}
            {onArchive && (
              <>
                <div className={`my-1 border-t ${theme.colors.border.primary}`} />
                <button
                  onClick={handleArchive}
                  className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${theme.colors.state.hover}`}
                >
                  <Archive className="w-3.5 h-3.5" /> Archivar
                </button>
              </>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}

export default ChatPanelHeader;
